import React from "react";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useLocation, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { alpha, useTheme } from "@mui/material/styles";
import { useAppSelector } from "../../store";
import { admin } from "../../features/session/sessionSlice";
import { isPreRegistrationEntryFlow } from "../../utils/registerFlow";

const REGISTER_LABELS: Record<string, string> = {
  "/register": "Inscription",
  "/pre-registration": "Pré-inscription",
  "/register/licence-validation": "Validation licence",
  "/register/select": "Choisir les tableaux",
  "/register/validation": "Validation",
  "/register/thanks": "Merci",
};

/** Pages publiques sous Layout : Accueil → page (aligné sur la nav principale). */
const PUBLIC_PAGE_LABELS: Record<string, string> = {
  "/boards": "Tableaux",
  "/rules": "Règlement",
  "/contactus": "Nous contacter",
  "/live": "Live",
  "/live-find": "Où jouez-vous ?",
  "/login": "Connexion",
  "/signup": "Créer un compte",
  "/reset-password": "Mot de passe oublié",
  "/confirm-reset-password": "Nouveau mot de passe",
};

const ADMIN_LABELS: Record<string, string> = {
  "/admin/registrations": "Liste des inscrits",
  "/admin/registration-list": "Liste des inscriptions",
  "/admin/board-registrations": "Inscrits par tableaux",
  "/admin/board-overlaps": "Croisements 2 tableaux",
  "/admin/tournament-managment": "Gérer le tournoi",
  "/admin/tournament-managment#remboursements": "Remboursements Stripe",
  "/admin/boards-managment": "Capacité et ouverture",
  "/admin/boards-definitions": "Définition des tableaux",
  "/admin/matchs": "Gérer les matchs",
  "/admin/checkin": "Pointage",
  "/admin/tables-occupancy": "Occupation des tables",
  "/admin/stats": "Statistiques",
  "/admin/admin-rights": "Droits admin",
  "/admin/tables-management": "Gestion des tables",
  "/admin/docs": "Documentation",
  "/admin/player-duplicates-audit": "Audit doublons joueurs",
  "/admin/board-nb-participants-audit": "Cohérence compteurs tableaux",
  "/admin/waitlist": "Listes d'attente",
};

/** Pages où le fil d'Ariane n'apporte pas (usage expert, écran de production). */
const PATHS_WITHOUT_BREADCRUMBS = new Set([
  "/admin/matchs",
  "/live",
  "/live-find",
]);

function getBreadcrumbs(
  pathname: string,
  hash: string,
  preRegistrationEntry: boolean
): { path: string; label: string }[] {
  if (PATHS_WITHOUT_BREADCRUMBS.has(pathname)) {
    return [];
  }

  if (pathname === "/pre-registration") {
    return [{ path: "/pre-registration", label: REGISTER_LABELS["/pre-registration"] }];
  }

  if (pathname.startsWith("/register")) {
    const firstStep = preRegistrationEntry ? "/pre-registration" : "/register";
    const steps = [
      firstStep,
      "/register/licence-validation",
      "/register/select",
      "/register/validation",
      "/register/thanks",
    ];
    const currentIndex = steps.findIndex((s) => pathname === s);
    const usedSteps =
      currentIndex >= 0 ? steps.slice(0, currentIndex + 1) : ["/register"];
    return usedSteps.map((path) => ({
      path,
      label: REGISTER_LABELS[path] ?? path,
    }));
  }

  if (pathname === "/inscrits") {
    return [
      { path: "/", label: "Accueil" },
      { path: "/inscrits", label: "Liste des inscrits" },
    ];
  }

  if (pathname.startsWith("/admin")) {
    const adminRouteKey = hash ? `${pathname}${hash}` : pathname;
    const label = ADMIN_LABELS[adminRouteKey] ?? ADMIN_LABELS[pathname] ?? "Admin";
    const breadcrumbPath = hash ? `${pathname}${hash}` : pathname;
    return [
      { path: "/admin/registrations", label: "Admin" },
      ...(pathname !== "/admin/registrations"
        ? [{ path: breadcrumbPath, label }]
        : []),
    ];
  }

  const publicLabel = PUBLIC_PAGE_LABELS[pathname];
  if (publicLabel) {
    return [
      { path: "/", label: "Accueil" },
      { path: pathname, label: publicLabel },
    ];
  }

  if (pathname.startsWith("/match/")) {
    return [
      { path: "/", label: "Accueil" },
      { path: pathname, label: "Match" },
    ];
  }

  return [];
}

/** Parcours inscription complet (utilisateur) : 5 étapes jusqu’à Merci */
const REGISTER_STEPS_FULL = [
  "/register",
  "/register/licence-validation",
  "/register/select",
  "/register/validation",
  "/register/thanks",
];

/** Parcours inscription admin : 4 étapes (sans page Merci) */
const REGISTER_STEPS_ADMIN = [
  "/register",
  "/register/licence-validation",
  "/register/select",
  "/register/validation",
];

const PageBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isAdmin = useAppSelector(admin);
  const preRegistrationEntry = isPreRegistrationEntryFlow();
  const items = getBreadcrumbs(
    location.pathname,
    location.hash,
    preRegistrationEntry
  );
  const isDark = theme.palette.mode === "dark";
  const isFirstStep = items.length === 1;

  const registerSteps = (() => {
    const base = isAdmin ? REGISTER_STEPS_ADMIN : REGISTER_STEPS_FULL;
    if (!preRegistrationEntry) return base;
    return ["/pre-registration", ...base.slice(1)];
  })();
  const registerStepIndex =
    (location.pathname.startsWith("/register") ||
      location.pathname === "/pre-registration") &&
    registerSteps.indexOf(location.pathname) >= 0
      ? registerSteps.indexOf(location.pathname) + 1
      : null;
  const totalRegisterSteps = registerSteps.length;

  if (items.length === 0) return null;

  const mutedOnBar = isDark
    ? alpha(theme.palette.common.white, 0.78)
    : theme.palette.text.secondary;

  /** Liens du fil : en sombre, le bleu primary.light reste trop sombre sur la barre. */
  const breadcrumbLinkSx = isDark
    ? {
        cursor: "pointer" as const,
        fontWeight: 600,
        color: alpha(theme.palette.common.white, 0.92),
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        textDecorationColor: alpha(theme.palette.common.white, 0.35),
        "&:hover": {
          color: theme.palette.common.white,
          textDecorationColor: alpha(theme.palette.common.white, 0.75),
        },
      }
    : {
        cursor: "pointer" as const,
        fontWeight: 600,
        color: theme.palette.primary.main,
        textDecoration: "none",
        textUnderlineOffset: "3px",
        "&:hover": {
          color: theme.palette.primary.dark,
          textDecoration: "underline",
        },
      };

  return (
    <Box
      sx={(t) => ({
        mb: 2,
        px: 2,
        py: 0.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor:
          t.palette.mode === "dark"
            ? alpha(t.palette.common.white, 0.14)
            : t.palette.divider,
        borderLeftWidth: 3,
        borderLeftColor: "primary.main",
        backgroundColor:
          t.palette.mode === "dark"
            ? alpha(t.palette.common.white, 0.08)
            : alpha(t.palette.common.black, 0.02),
      })}
    >
      {isFirstStep && registerStepIndex != null ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            sx={(t) => ({
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.875rem",
              fontWeight: 700,
            })}
          >
            {registerStepIndex}
          </Box>
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {items[0].label}
          </Typography>
          <Typography variant="body2" sx={{ color: mutedOnBar, fontWeight: 500 }}>
            Étape {registerStepIndex} sur {totalRegisterSteps}
          </Typography>
        </Box>
      ) : (
        <MuiBreadcrumbs
          aria-label="Fil d'Ariane"
          separator={
            <NavigateNextIcon
              fontSize="small"
              sx={{
                color: isDark
                  ? alpha(theme.palette.common.white, 0.5)
                  : theme.palette.text.secondary,
              }}
            />
          }
          sx={{
            px: 0,
            "& .MuiBreadcrumbs-separator": { mx: 0.5 },
            "& .MuiBreadcrumbs-li": { display: "flex", alignItems: "center" },
          }}
        >
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const stepNum =
              registerStepIndex != null
                ? registerSteps.indexOf(item.path) + 1
                : null;
            return isLast ? (
              <Box key={item.path} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {stepNum != null && (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {stepNum}
                  </Box>
                )}
                <Typography color="text.primary" variant="body2" fontWeight={500}>
                  {item.label}
                </Typography>
              </Box>
            ) : (
              <Link
                key={item.path}
                component="button"
                variant="body2"
                onClick={() => navigate(item.path)}
                sx={breadcrumbLinkSx}
              >
                {item.label}
              </Link>
            );
          })}
        </MuiBreadcrumbs>
      )}
    </Box>
  );
};

export default PageBreadcrumbs;
